import React, { FunctionComponent, useRef, useState } from 'react';
import { I18n } from 'common/i18n';
import { mapPermissionScopeToTargetAudience, withGuidanceV2 } from 'common/core/approvals';
import getOnlyMatchingWorkflow from 'common/core/approvals/guidanceHelpers/getOnlyMatchingWorkflow';
import {
  showToastNow,
  showToastOnPageReload,
  ToastType,
} from 'common/components/ToastNotification/Toastmaster';
import type { GuidanceSummaryV2 } from 'common/types/approvals';
import { ForgeButton } from '@tylertech/forge-react';
import { GuidanceBooleanCheckSummary, GuidanceSummaryV2Helper } from 'common/core/approvals/Types';
import { ForgePopover } from '@tylertech/forge-react';
import { View } from 'common/types/view';
import { assign, pathname } from 'common/window_location';

const toastI18nScope = 'shared.components.asset_action_bar.publication_action';
const buttonI18nScope = `${toastI18nScope}.draft.primary_action_text`;

interface SubmitForApprovalButtonProps {
  approvalsGuidance: GuidanceSummaryV2;
  disabled: boolean;
  disabledReason?: string | JSX.Element;
  view: View;
}

const SubmitForApprovalButton: FunctionComponent<SubmitForApprovalButtonProps> = ({
  approvalsGuidance,
  disabled,
  disabledReason,
  view,
}: SubmitForApprovalButtonProps) => {
  const targetRef = useRef<any>();
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const approvalsHelper: GuidanceSummaryV2Helper = withGuidanceV2(approvalsGuidance);
  const willEnterApprovalQueueSummary: GuidanceBooleanCheckSummary =
    approvalsHelper.summarizeWillEnterApprovalQueue();

  const onClick = () => {
    setBusy(true);

    const targetAudienceFromPerms = mapPermissionScopeToTargetAudience(view.permissions?.scope);
    let targetAudience;
    if (targetAudienceFromPerms) {
      targetAudience = targetAudienceFromPerms;
    } else {
      targetAudience = getOnlyMatchingWorkflow(willEnterApprovalQueueSummary);
    }
    let redirectTo = `/d/${approvalsHelper.publishedUid()}`;
    let successToastContent = I18n.t('shared.site_chrome.access_manager.publish.success_toast');

    if (
      targetAudience &&
      willEnterApprovalQueueSummary.length &&
      willEnterApprovalQueueSummary[targetAudience]
    ) {
      redirectTo = pathname();
      successToastContent = I18n.t('submitted_asset_for_approval', { scope: toastI18nScope });
    }

    // targetAudience could be undefined. We want it to error if it is.
    if (targetAudience) {
      approvalsHelper
        .submitForApproval(targetAudience)
        .then(() => {
          showToastOnPageReload({
            type: ToastType.SUCCESS,
            content: successToastContent,
          });
          assign(redirectTo);
        })
        .catch((err: any) => {
          console.error(err);
          showToastNow({
            type: ToastType.ERROR,
            content: I18n.t('unknown_error', { scope: toastI18nScope }),
          });
        });
    } else {
      throw new Error(
        'SubmitForApprovalButton - Submit action initiated without a specified target audience',
      );
    }
  };

  let title = '';
  if (willEnterApprovalQueueSummary.public) {
    title = I18n.t('updating_public_asset_requires_approval', { scope: buttonI18nScope });
  } else if (willEnterApprovalQueueSummary.internal) {
    title = I18n.t('updating_internal_asset_requires_approval', { scope: buttonI18nScope });
  }

  const buttonTextKey = willEnterApprovalQueueSummary.length ? 'submit_for_approval' : 'previously_published';

  return (
    <div onMouseEnter={() => setFlyoutOpen(true)} onMouseLeave={() => setFlyoutOpen(false)}>
      <ForgeButton
        onClick={onClick}
        variant="raised"
        data-testid="aab-submit-for-approval"
        ref={targetRef}
        disabled={disabled || busy}
        title={title}
      >
        {I18n.t(buttonTextKey, { scope: buttonI18nScope })}
      </ForgeButton>
      {disabledReason && (
        <ForgePopover
          targetElementRef={targetRef}
          open={flyoutOpen}
          onDismiss={() => {}}
          options={{ placement: 'bottom-end' }}
        >
          <div className="forge-typography--body1">
            <div className="publish-button-flyout-content">{disabledReason}</div>
          </div>
        </ForgePopover>
      )}
    </div>
  );
};

export default SubmitForApprovalButton;
